// frontend/src/components/homepage/heroData.js
//
// The demo corpus the live-mesh homepage renders: the lead cards in the hero
// stage, the ticker, the pinned live feed and the mesh's ignite tags all read
// from HERE, so the page can never show a profession in one place that it does
// not show in another.
//
// EVERY STRING IS INVENTED DEMO CONTENT. Nothing in this file is a real post, a
// real group or a real customer - they are illustrations of what the product
// finds, written to read like the genuine article. Copy is owner-approved
// verbatim from the design prototype; do not paraphrase it here.
//
// Wording rule that applies to this file specifically (CLAUDE.md): this is
// in-app/marketing surface copy shown to a visitor, so it uses "הזדמנות", and
// the SEO-facing prose elsewhere on the page carries "לידים". The group prefix
// "פורסם ב:" is part of the approved card design.

/**
 * The lead cards. `score` >= 92 renders as HOT (ember), below that as a normal
 * teal match - the same threshold the ticker uses for its hot dot.
 */
export const LEADS = [
  {
    pro: "פיתוח אתרים",
    grp: "פורסם ב: יזמים ובעלי עסקים תל אביב",
    txt: "מחפש מפתח/ת אתרים לבניית אתר תדמית לעסק החדש שלי, אשמח להמלצות על מישהו אמין ומקצועי.",
    city: "תל אביב",
    time: "עכשיו",
    score: 90,
  },
  {
    pro: 'עו"ד דיני עבודה',
    grp: "פורסם ב: עסקים בדרום",
    txt: "מחפש עורך דין תותח שמתמחה בדיני עבודה מאזור באר שבע, אשמח להמלצות!",
    city: "באר שבע",
    time: "לפני 2 דקות",
    score: 94,
  },
  {
    pro: "מכון קוסמטיקה",
    grp: "פורסם ב: נשים ברעננה והסביבה",
    txt: "מחפשת קוסמטיקאית מקצועית באזור לטיפולי פנים, אשמח להמלצות אמיתיות 🙏",
    city: "רעננה",
    time: "לפני 3 דקות",
    score: 95,
  },
  {
    pro: "אילוף כלבים",
    grp: "פורסם ב: הורים לכלבים רמת גן",
    txt: "רוצה להתחיל תהליך אילוף עם הגור החדש שלנו, מישהו מכיר מאלף/ת כלבים מומלץ/ת באזור?",
    city: "רמת גן",
    time: "לפני 8 דקות",
    score: 92,
  },
  {
    pro: "סוכן ביטוח",
    grp: "פורסם ב: חיפה והקריות - המלצות",
    txt: "מחפשת סוכן/ת ביטוח שיעשה סדר בכל הפוליסות, מרגישה שאני משלמת יותר מדי. המלצות?",
    city: "חיפה",
    time: "לפני 5 דקות",
    score: 91,
  },
  {
    pro: "שיווק לעסקים",
    grp: "פורסם ב: עסקים קטנים ירושלים",
    txt: "בעל עסק קטן, מחפש מי שיעזור לי עם השיווק והנוכחות ברשת - מאיפה כדאי להתחיל?",
    city: "ירושלים",
    time: "לפני 6 דקות",
    score: 89,
  },
  {
    pro: "מניקור לק ג'ל",
    grp: "פורסם ב: השרון - המלצות",
    txt: "עברתי דירה לאזור השרון ומחפשת מקום טוב ללק ג'ל, מישהי מכירה מניקוריסטית מומלצת?",
    city: "כפר סבא",
    time: "לפני 10 דקות",
    score: 88,
  },
  {
    pro: "צלם אירועים",
    grp: "פורסם ב: מתחתנים בצפון",
    txt: "מתחתנים בקיץ ומחפשים צלם/ת לחתונה בסגנון טבעי ואותנטי, אשמח להמלצות 🙏",
    city: "טבריה",
    time: "לפני 12 דקות",
    score: 90,
  },
];

/** Score at or above which a lead renders as HOT in the ticker. */
export const HOT_SCORE = 92;

/**
 * Score at or above which the HERO card stamps a lead HOT.
 *
 * Deliberately LOWER than the ticker's threshold, and that is the design's
 * choice, not a drift. The ticker is a dense list where "hot" has to mean
 * genuinely exceptional to carry any signal at all; the hero shows ONE card at a
 * time and the stamp is the moment the card is selling - at 92 it would only
 * ever fire on three of the eight leads and most visitors would never see it.
 */
export const HERO_HOT_SCORE = 90;

/**
 * The irrelevant posts the pinned "how it works" feed scrolls past to show what
 * the analyzer discards. Not used by the hero.
 */
export const NOISE = [
  {
    grp: "פורסם ב: שוק יד שנייה",
    txt: "למכירה ספה תלת מושבית במצב מצוין, איסוף מרמת השרון",
  },
  {
    grp: "פורסם ב: הורים בגבעתיים",
    txt: "מישהו יודע אם הגן ברחוב הרצל פתוח מחר?",
  },
  {
    grp: "פורסם ב: מטיילים בצפון",
    txt: "המלצות למסלול קצר עם ילדים באזור הגליל?",
  },
  {
    grp: "פורסם ב: אוכל ומסעדות",
    txt: "איפה אוכלים חומוס טוב באזור יפו? חשוב שיהיה פתוח בשישי",
  },
];

/**
 * The professions the mesh names when a node ignites.
 *
 * These are advertised on a public page as things we match, so each one must
 * correspond to a real entry in the professions catalogue - the page must never
 * name a profession the product does not actually match. LIST PENDING OWNER
 * APPROVAL (carried over from the prototype's own note).
 */
export const FIELDS = [
  'עו"ד דיני עבודה',
  "מכון קוסמטיקה",
  "אילוף כלבים",
  "פיתוח אתרים",
  "סוכן ביטוח",
  "שיווק לעסקים",
  "מניקור לק ג'ל",
  "צלם אירועים",
  "ייעוץ עסקי",
  "עיצוב פנים",
  "שיפוצים",
  "רואה חשבון",
  "מאמן כושר אישי",
  "יועץ משכנתאות",
  "חשמלאי",
  "מאפרת כלות",
  "שיווק דיגיטלי",
  "יועץ השקעות",
  "יועץ קריפטו",
  "מלווה פיננסי",
  "שף פרטי",
  "מנעולן",
  "ביטוח רכב",
  "ביטוח דירה",
  "ביטוח משכנתא",
  "נוטריון",
  'עו"ד נזיקין',
];
